import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type AccessGroup from 'modules/core/store/models/AccessGroup'

import type { Employees } from 'modules/employees/store/collections/employees'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import questionGroups from 'modules/surveys/store/collections/questionGroups'
import { Status } from 'modules/surveys/store/models/v2/Survey'
import type { Users } from 'modules/core/store/collections/users'

interface Props {
  accessGroup: AccessGroup
  surveyId: string
  surveyStatus: string
  back: string
  users: Users
  employees: Employees
}

const ManualNewRespondent = ({ accessGroup, surveyId, surveyStatus, back }: Props) => {
  const { updateCanBeStarted } = useSurveyShowContext()
  const [userIds, setUserIds] = useState<string[]>([])
  const [running, setRunning] = useState(false)
  const navigate = useNavigate()
  const onClose = () => navigate(back)

  const createAccessGroupMemberships = async () =>
    await formAccessGroupMemberships.bulkCreate({
      access_ids: userIds,
      access_group_id: accessGroup.attributes.id,
      form_id: surveyId,
    })

  const createQuestionGroups = async () =>
    await questionGroups.bulkCreate({
      form_id: surveyId,
      accesses_ids: userIds,
      access_group_id: accessGroup.attributes.id,
    })

  const submit = async (closePortal: ClosePortalFn) => {
    setRunning(true)

    const { success } = await asyncAction(() =>
      surveyStatus === Status.draft ? createAccessGroupMemberships() : createQuestionGroups()
    )
    setRunning(false)

    if (success) {
      if (surveyStatus !== Status.draft) {
        formAccessGroupMemberships.fetch({
          data: {
            form_id: surveyId,
            access_group_id: accessGroup.attributes.id,
          },
        })
      }
      if (surveyStatus !== Status.active) await updateCanBeStarted()
      closePortal()
    }
  }

  formAccessGroupMemberships.fetch({
    data: {
      access_group_id: accessGroup.attributes.id,
      form_id: surveyId,
    },
  })

  const memberIds = formAccessGroupMemberships.map((m) => m.attributes.access_id)

  return (
    <Modal
      title={i18n.t('surveys.form.participants.new.title')}
      description={i18n.t('surveys.form.participants.new.description.survey')}
      illustration="surveys"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <Button disabled={!userIds.length || running} type="primary" submit>
          {i18n.t('surveys.form.participants.new.submit')}
        </Button>
      }
    >
      <EmployeeSelectorV2
        multiple
        useAccessId
        value={userIds}
        onChange={(state) => {
          if (state.type === 'manual') {
            setUserIds(state.ids)
          }
        }}
        forcedFilters={{ excludeIds: memberIds }}
        options={{ disableSelectAll: true }}
      />
    </Modal>
  )
}

export default observer(ManualNewRespondent)
